import * as React from 'react';

import type * as api from '@gitbook/api-client';
import {
    Description,
    Dialog,
    DialogHeader,
    DialogRoot,
    Heading,
    LoadingIndicator,
    useDialogClose,
} from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { SpaceCreationDialog } from '../../Space';
import { resolvePageTarget } from '../../Space/creation/pageTree';
import { CreateSiteSectionForm, type CreateSiteSectionFormProps } from './CreateSiteSectionForm';
import { useSpaceRevisions } from './useSpaceRevisions';

export interface CreateSiteSectionDialogProps {
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    inSiteSectionGroup?: api.SiteSectionGroup | null;
    onCreateSection?: (siteSection: api.SiteSection) => void;
}

export function CreateSiteSectionDialog(props: CreateSiteSectionDialogProps) {
    const [sectionSelection, setSectionSelection] = React.useState<{
        space: api.Space | null;
        template: api.SpaceTemplate | null;
        pageId: string | null;
    } | null>(null);
    const persistentSectionSelection = usePersistentValue(sectionSelection);
    const close = useDialogClose();

    return (
        <>
            <SpaceCreationDialog
                organizationId={props.organization.id}
                siteContentLayout
                target={{
                    type: 'siteSection',
                    siteStructure: props.siteStructure,
                    props: {
                        site: props.site,
                        onCreateSpace: (space, options) =>
                            setSectionSelection({
                                space,
                                template: null,
                                pageId: options.pageId ?? null,
                            }),
                        onSelectBlankSpace: () =>
                            setSectionSelection({
                                space: null,
                                template: null,
                                pageId: null,
                            }),
                        onSelectTemplate: (template) =>
                            setSectionSelection({
                                space: null,
                                template,
                                pageId: null,
                            }),
                    },
                }}
            />
            {persistentSectionSelection ? (
                <DialogRoot
                    isOpen={Boolean(sectionSelection)}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setSectionSelection(null);
                            close();
                        }
                    }}
                >
                    <CreateSiteSectionDetailsDialog
                        organization={props.organization}
                        site={props.site}
                        siteStructure={props.siteStructure}
                        inSiteSectionGroup={props.inSiteSectionGroup}
                        onCreateSection={props.onCreateSection}
                        space={persistentSectionSelection.space}
                        template={persistentSectionSelection.template}
                        pageId={persistentSectionSelection.pageId}
                    />
                </DialogRoot>
            ) : null}
        </>
    );
}

interface CreateSiteSectionDetailsDialogProps extends CreateSiteSectionFormProps {
    siteStructure: api.SiteStructure;
    pageId?: string | null;
}

function CreateSiteSectionDetailsDialog(props: CreateSiteSectionDetailsDialogProps) {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Name the section</Heading>
                <Description>One last step before adding the section.</Description>
            </DialogHeader>
            {props.pageId && props.space ? (
                <React.Suspense
                    fallback={
                        <div className="flex justify-center p-10">
                            <LoadingIndicator size="large" />
                        </div>
                    }
                >
                    <SectionForm {...props} space={props.space} />
                </React.Suspense>
            ) : (
                <CreateSiteSectionForm {...props} />
            )}
        </Dialog>
    );
}

function SectionForm(props: CreateSiteSectionDetailsDialogProps & { space: api.Space }) {
    const { space, pageId } = props;
    const revisions = useSpaceRevisions([space]);
    const defaultTitle = resolvePageTarget(revisions.get(space.id), pageId)?.title;
    return <CreateSiteSectionForm {...props} defaultTitle={defaultTitle} />;
}
